import { Disclosure } from "@headlessui/react";
import { ChevronUpIcon } from "@heroicons/react/20/solid";
import { AnimatePresence, motion, useInView } from "framer-motion";
import React, { useEffect, useRef, useState } from "react";
import { IconType } from "react-icons";
import { AiOutlineOpenAI } from "react-icons/ai";
import { DiAndroid, DiCodeBadge } from "react-icons/di";
import {
  FaAws,
  FaCloud,
  FaDatabase,
  FaLinux,
  FaPython,
  FaReact,
} from "react-icons/fa";
import { GrVulnerability } from "react-icons/gr";
import { IoLogoJavascript } from "react-icons/io5";
import { RiFlutterFill } from "react-icons/ri";
import { SiAdobe, SiApple, SiKotlin } from "react-icons/si";
import { Tooltip } from "react-tooltip";
import { useContact } from "../contexts/contactContext";
import useSmoothScroll from "../hooks/useSmoothScroll";
import { STANDARD_VARIANTS } from "../constants/animations";
import { CONTAINER_STYLES, TYPOGRAPHY_STYLES, CARD_STYLES } from "../constants/styles";

const minWidthForDesktop = 768;

interface Service {
  title: string;
  description: string;
  icons: IconType[];
}

const services: Service[] = [
  {
    title: "Mobile App Development",
    description:
      "Build scalable mobile applications that reach millions. From concept to 20M+ downloads, I deliver apps that users love and businesses profit from. iOS, Android, and cross-platform solutions.",
    icons: [RiFlutterFill, SiKotlin, DiAndroid, SiApple],
  },
  {
    title: "AI & Machine Learning Solutions",
    description:
      "Transform your business with cutting-edge AI. From NLP systems to intelligent automation, I architect AI solutions that drive real ROI and competitive advantage.",
    icons: [AiOutlineOpenAI, FaPython, FaCloud, FaDatabase],
  },
  {
    title: "Full-Stack Web Development",
    description:
      "End-to-end web solutions that scale. Responsive frontends, robust backends, and seamless user experiences. Built for performance, security, and growth.",
    icons: [FaReact, IoLogoJavascript, FaPython, FaAws, SiAdobe],
  },
  {
    title: "Cybersecurity & Infrastructure",
    description:
      "Military-grade security solutions. Protect your assets with enterprise-level cybersecurity, vulnerability assessment, and secure architecture design from an ex-8200 expert.",
    icons: [FaCloud, DiCodeBadge, GrVulnerability, FaLinux],
  },
];

const iconNames: { [key: string]: string } = {
  DiGo: "Go",
  FaGolang: "Golang",
  FaPython: "Python",
  FaLinux: "Linux",
  FaCloud: "Network Security",
  RiFlutterFill: "Flutter/Dart",
  SiKotlin: "Kotlin + Compose",
  SiJetpackcompose: "Jetpack Compose",
  SiAndroidstudio: "Android Studio",
  FaReact: "ReactJS",
  RiTailwindCssFill: "Tailwind CSS",
  IoLogoJavascript: "JavaScript/TypeScript",
  FaNodeJs: "Node.js",
  FaDatabase: "SQL/Redis",
  GrVulnerability: "Vulnerability Research",
  DiCodeBadge: "Cyber-Oriented Development",
  FaAws: "AWS",
  AiOutlineOpenAI: "AI Integration",
  SiAdobe: "Adobe Suite",
  SiApple: "iOS",
  DiAndroid: "Android",
  FaFigma: "Figma",
};

const IconsDisplay: React.FC<{ icons: IconType[] }> = ({ icons }) => (
  <div className="flex mt-8 space-x-4 justify-center">
    {icons.map((Icon, index) => (
      <div key={index} className="relative">
        <Icon
          data-tooltip-id={`icon-tooltip-${index}`}
          data-tooltip-content={iconNames[Icon.name]}
          className="w-7 h-7 hover:scale-125 transition-all duration-100 ease-in-out text-primary-500 dark:text-primary-400"
        />
        <Tooltip id={`icon-tooltip-${index}`} />
      </div>
    ))}
  </div>
);

const CardContent: React.FC<Service & { isDesktop: boolean }> = ({
  title,
  description,
  icons,
  isDesktop,
}) => (
  <div className="px-6 py-2 flex flex-col h-full duration-100 transition-all ease-in-out">
    {isDesktop ? (
      <>
        <h2 className="text-2xl font-semibold mb-4 leading-tight">{title}</h2>
        <p className="flex-grow">{description}</p>
        <IconsDisplay icons={icons} />
      </>
    ) : (
      <Disclosure>
        {({ open }) => (
          <>
            <Disclosure.Button className="flex justify-between w-full py-4 text-xl font-medium text-left focus:outline-none focus-visible:ring focus-visible:ring-gray-500 focus-visible:ring-opacity-75">
              <span>{title}</span>
              <ChevronUpIcon
                className={`${!open ? "transform rotate-180" : ""
                  } w-5 h-5 text-gray-500 transition-transform duration-200`}
              />
            </Disclosure.Button>
            <AnimatePresence>
              {open && (
                <Disclosure.Panel static>
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className="text-gray-800 dark:text-gray-200">
                      {description}
                      <IconsDisplay icons={icons} />
                    </div>
                  </motion.div>
                </Disclosure.Panel>
              )}
            </AnimatePresence>
          </>
        )}
      </Disclosure>
    )}
  </div>
);

const TechStack: React.FC = () => {
  const { setTitle, setMessage } = useContact();
  const scrollToElement = useSmoothScroll();

  const handleServiceClick = (serviceTitle: string) => {
    setTitle(`Inquiry: ${serviceTitle} services`);
    setMessage(
      `Hi Tomer,\nI am interested in your services regarding ${serviceTitle}`
    );
    scrollToElement("contact");
  };

  const [isDesktop, setIsDesktop] = useState(
    window.innerWidth >= minWidthForDesktop
  );

  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });

  useEffect(() => {
    const handleResize = () =>
      setIsDesktop(window.innerWidth >= minWidthForDesktop);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);


  return (
    <motion.div
      ref={ref}
      className={CONTAINER_STYLES.full}
      animate={isInView ? "visible" : "hidden"}
      variants={STANDARD_VARIANTS.container}
    >
      <motion.h1
        className={TYPOGRAPHY_STYLES.sectionTitle}
        variants={STANDARD_VARIANTS.item}
      >
        Services That Drive Results
      </motion.h1>
      <motion.p
        className={`${TYPOGRAPHY_STYLES.sectionSubtitle} mb-4`}
        variants={STANDARD_VARIANTS.item}
      >
        From startups to Fortune 500 companies, I deliver technology solutions that generate measurable business impact.
      </motion.p>
      <motion.p
        className="text-lg text-center mb-12 dark:text-accent-400 text-accent-600"
        variants={STANDARD_VARIANTS.item}
      >
        Ready to transform your ideas into profitable products?{" "}
        <a
          href="#contact"
          onClick={(e) => {
            e.preventDefault();
            scrollToElement("contact");
          }}
          className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-semibold cursor-pointer underline decoration-2 underline-offset-2"
        >
          Let's discuss your project
        </a>
      </motion.p>
      <motion.div
        className={`xl:mx-64 grid ${"md:px-0 px-8 md:grid-cols-2 grid-cols-1 gap-6"}`}
        variants={STANDARD_VARIANTS.container}
      >
        {services.map((service, index) => (
          <motion.div
            key={index}
            className={`${isDesktop ? "hover:scale-105 hover:shadow-xl" : ""
              } transition-all duration-300 ease-in-out py-8 ${CARD_STYLES.base} dark:text-surface-50 text-accent-900 overflow-hidden cursor-pointer`}
            onClick={() => handleServiceClick(service.title)}
            variants={STANDARD_VARIANTS.item}
          >
            <CardContent {...service} isDesktop={isDesktop} />
          </motion.div>
        ))}
      </motion.div>
    </motion.div>
  );
};

export default TechStack;
